import React, { useEffect, useRef, useState } from 'react';
import {
  decideScan,
  addScan,
  remainingQuantity,
  isLineComplete,
  weightFromCode,
  parseWeight,
  formatLineQuantity,
} from '../utils/loadingScan';
import { cartLabels } from '../utils/carts';
import { playTone } from '../utils/scanTone';
import { loadSavedLoad, saveLoad, clearSavedLoad, restoreScans } from '../utils/cargaBetaStorage';
import CargaBetaFeedback from './CargaBetaFeedback';
import '../styles/productos.css';

// HH:MM; es-CL alone would print 12-hour time with "p. m.".
const savedTime = (iso) => new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });

// Beta #1 of the loading screen (Productos para el Pedido). The loader checks
// what goes on the truck with the scanner gun alone: the first beep
// identifies a product, the second beep of the same code registers what its
// line is missing, or the weight of a variable-weight piece.
function CargaBeta({ ponumber, order, offline, onClose, onContinue }) {
  const items = order.items || [];
  // What this device kept of the load, read once when the beta opens.
  const [start] = useState(() => {
    const saved = loadSavedLoad(ponumber);
    const scans = saved ? restoreScans(items, saved.scans) : [];
    return { scans, savedAt: scans.length ? saved.savedAt : null };
  });
  const [scannedItems, setScannedItems] = useState(start.scans);
  const [saveFailed, setSaveFailed] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [weight, setWeight] = useState('');
  // What the panel shows; see CargaBetaFeedback for the shapes.
  const [feedback, setFeedback] = useState(null);
  const barcodeInput = useRef(null);
  const weightInput = useRef(null);

  // Every change of the load is kept on the device; an empty load leaves
  // nothing behind, so the next visit opens the normal view.
  useEffect(() => {
    if (scannedItems.length === 0) {
      clearSavedLoad(ponumber);
      setSaveFailed(false);
      return;
    }
    const scans = scannedItems.map(({ sku_code, scanned_quantity }) => ({ sku_code, scanned_quantity }));
    setSaveFailed(!saveLoad(ponumber, { scans, order }));
  }, [scannedItems, ponumber, order]);

  const identified = feedback && feedback.kind === 'identified' ? feedback : null;

  const scannedQuantity = (skuCode) => {
    const scanned = scannedItems.find((s) => s.sku_code === skuCode);
    return scanned ? scanned.scanned_quantity : 0;
  };

  const focusBarcode = () => {
    if (barcodeInput.current) barcodeInput.current.focus();
  };

  const identify = (line, code) => {
    const readWeight = line.format_unit_kg ? weightFromCode(code) : null;
    setFeedback({ kind: 'identified', line, code, readWeight, weightProblem: null });
    setWeight(readWeight === null ? '' : String(readWeight));
  };

  // Registers the identified line: what a unit line is missing, or the weight
  // field of a kilo line. Returns false when that weight is missing or wrong:
  // a missing one is asked for in its field; a wrong one (typically a scan
  // that landed there) goes back to the weight read, and the gun gets the
  // focus again so the next scan doesn't pile onto it.
  const register = (line) => {
    let quantity;
    if (line.format_unit_kg) {
      quantity = parseWeight(weight);
      if (quantity === null) {
        const problem = weight.trim() ? 'invalid' : 'missing';
        setFeedback({ ...identified, weightProblem: problem });
        if (problem === 'missing') {
          if (weightInput.current) weightInput.current.focus();
        } else {
          setWeight(identified.readWeight === null ? '' : String(identified.readWeight));
          focusBarcode();
        }
        return false;
      }
    } else {
      quantity = remainingQuantity(line, scannedQuantity(line.sku_code));
    }
    setScannedItems(addScan(scannedItems, line, quantity));
    setFeedback({ kind: 'registered', item: line, quantity, before: scannedItems });
    setWeight('');
    return true;
  };

  // The gun types the code and sends Enter. The field handles the key itself:
  // once the weight field shows, the form has two fields and no submit
  // button, and a browser no longer submits it on Enter.
  const handleBarcodeKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleScan();
  };

  const handleScan = () => {
    const code = barcode.trim();
    setBarcode('');
    if (!code) return;
    const decision = decideScan({
      items, code, identifiedCode: identified ? identified.code : null, scannedQuantity,
    });
    if (decision.type === 'rejected') {
      setFeedback({ kind: 'rejected', code });
      playTone('error');
    } else if (decision.type === 'complete') {
      setFeedback({ kind: 'complete', line: decision.line });
      playTone('warning');
    } else if (decision.type === 'confirm') {
      register(decision.line);
    } else {
      identify(decision.line, code);
    }
  };

  // Once the loader edits the weight the focus is out of the code field, so
  // the edited weight is confirmed with Enter (or Agregar), not with the gun.
  const handleWeightKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (identified && register(identified.line)) focusBarcode();
  };

  const handleAdd = () => {
    if (identified && register(identified.line)) focusBarcode();
  };

  const handleUndo = () => {
    setScannedItems(feedback.before);
    setFeedback(null);
    focusBarcode();
  };

  const removeScannedItem = (skuCode) => {
    setScannedItems(scannedItems.filter((s) => s.sku_code !== skuCode));
    setFeedback(null);
    focusBarcode();
  };

  // Every button hands the focus back to the code field: a button that kept
  // it would be pressed again by the gun's Enter (a cancelled Reiniciar would
  // reopen its confirm, where Enter means OK and wipes the load).
  const handleRestart = () => {
    if (window.confirm('¿Reiniciar la carga? Se borra lo escaneado de este pedido.')) {
      setScannedItems([]);
      setFeedback(null);
      setWeight('');
    }
    focusBarcode();
  };

  const allLoaded = items.every((item) => isLineComplete(item, scannedQuantity(item.sku_code)));
  const askWeight = Boolean(identified && identified.line.format_unit_kg);

  return (
    <div className="productos-container">
      <div className="beta-scanner-container">
        <div className="beta-header">
          <button type="button" onClick={onClose} className="back-button">
            ←
          </button>
          <h1>Verificación por Código de Barras - PO {ponumber}</h1>
          <span className="beta-badge">BETA</span>
        </div>

        {(order.carts || []).length > 0 && (
          <ul className="carga-beta-carts" aria-label="Carros">
            {cartLabels(order.carts).map((cart) => (
              <li key={cart.code} className="cart-item">
                <strong>{cart.kind}</strong> {cart.number}
              </li>
            ))}
          </ul>
        )}

        {start.savedAt && (
          <p className="carga-beta-note">Carga recuperada · guardada {savedTime(start.savedAt)}</p>
        )}
        {offline && (
          <p className="carga-beta-note carga-beta-note--warning">Sin conexión: usando el pedido guardado</p>
        )}
        {saveFailed && (
          <p className="carga-beta-note carga-beta-note--warning">No se pudo guardar el avance en este equipo</p>
        )}

        <form onSubmit={(e) => e.preventDefault()} className="barcode-form">
          <div className={`barcode-inputs${askWeight ? '' : ' barcode-inputs--single'}`}>
            <div className="input-group">
              <label htmlFor="barcode">Código de Barras:</label>
              <input
                id="barcode"
                ref={barcodeInput}
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={handleBarcodeKeyDown}
                placeholder="Escanee el código"
                autoComplete="off"
                autoFocus
              />
            </div>
            {askWeight && (
              <div className="input-group">
                <label htmlFor="weight">Peso (kg):</label>
                <input
                  id="weight"
                  ref={weightInput}
                  type="number"
                  step="any"
                  min="0"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  onKeyDown={handleWeightKeyDown}
                />
              </div>
            )}
            <button type="button" className="scan-button" onClick={handleAdd} disabled={!identified}>
              Agregar
            </button>
          </div>
        </form>

        <CargaBetaFeedback feedback={feedback} scannedQuantity={scannedQuantity} onUndo={handleUndo} />

        <div className="items-progress">
          <h3>Progreso del Pedido ({scannedItems.length}/{items.length} productos verificados)</h3>
          <div className="items-grid">
            {items.map((item, index) => {
              const scannedQty = scannedQuantity(item.sku_code);
              const isComplete = isLineComplete(item, scannedQty);
              return (
                <div
                  key={index}
                  className={`item-card ${isComplete ? 'complete' : scannedQty > 0 ? 'partial' : 'pending'}`}
                >
                  <div className="item-info">
                    <h4>{item.sku_name}</h4>
                    <p>SKU: {item.sku_code}</p>
                    <p>Pedido: {formatLineQuantity(item, item.ordered_quantity)}</p>
                    {item.format_unit_kg && <p>Pickeado: {formatLineQuantity(item, item.actual_quantity)}</p>}
                    <p>Verificado: {formatLineQuantity(item, scannedQty)}</p>
                    {item.sku_frozen && <span className="frozen-badge">❄️ Frío</span>}
                  </div>
                  <div className="item-status">
                    {isComplete ? (
                      <span className="status-complete">✓</span>
                    ) : scannedQty > 0 ? (
                      <span className="status-partial">◐</span>
                    ) : (
                      <span className="status-pending">○</span>
                    )}
                  </div>
                  {scannedQty > 0 && (
                    <button type="button" onClick={() => removeScannedItem(item.sku_code)} className="remove-item">
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="beta-actions">
          {scannedItems.length > 0 && (
            <button type="button" className="carga-beta-restart" onClick={handleRestart}>
              Reiniciar carga
            </button>
          )}
          {allLoaded ? (
            <button type="button" onClick={() => onContinue(scannedItems)} className="complete-order-button">
              Continuar a Finalización
            </button>
          ) : (
            <p className="pending-message">Escanee todos los productos para continuar</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default CargaBeta;
